import { FC, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable, DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { MultiSelect } from 'primereact/multiselect'
import { toast } from 'react-hot-toast'

import { useForceCompleteStarccmTasks, useGetStalledStarccmTasks } from '../../api/failed-tasks-API'
import { MultiSelectCheckboxStyle, TablePaginatorStyle } from '../../utils/styling-constants'

type Evidence = 'LIKELY_COMPLETE' | 'ENDED_NO_ATP' | 'STILL_RUNNING' | 'NO_EVIDENCE'

type NextStep = {
  workflowSystemRunId: string
  workflowName?: string
  taskSystemRunId: string
  taskName?: string
}

type StalledRow = {
  submissionId: string
  submissionName?: string
  projectName?: string
  caseId?: string
  caseNumber?: string
  caseName?: string
  workflowRunGroupSysId?: string
  workflowSystemRunId?: string
  workflowName?: string
  taskId?: string
  taskName?: string
  status?: string
  runningSince?: number
  evidence: Evidence
  evidenceError?: string
  liveLogName?: string
  latestLogName?: string
  latestLogTimestamp?: number
  latestLogModified?: number
  atpReportName?: string
  atpModified?: number
  atpFresh?: boolean
  nextSteps?: NextStep[]
  nextStepReason?: string
  lastSubmittedBy?: string
}

type FilterForm = {
  submissionId: string
  caseId: string
  statuses: string[]
  submissionDateRange: (Date | null)[] | null
  includeAll: boolean
}

type ForceCompleteResponse = {
  succeeded?: number
  failed?: number
  nextStepPending?: number
  results?: Array<{
    taskId?: string
    caseId?: string
    success: boolean
    message: string
    nextStepPending?: boolean
  }>
}

const STATUS_OPTIONS = [
  { label: 'Running', value: 'RUNNING' },
  { label: 'Waiting', value: 'WAITING' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Error', value: 'ERROR' },
  { label: 'Warn on Error', value: 'WARN_ON_ERROR' },
  { label: 'Cancelled', value: 'CANCELLED' }
]

// The platform only accepts a completion for these after the task is set back to Running.
const RESET_STATUSES = ['ERROR', 'WARN_ON_ERROR']

const statusLabel = (status?: string) =>
  STATUS_OPTIONS.find(o => o.value === status)?.label ?? status ?? '—'

const DEFAULT_FILTER_VALUES: FilterForm = {
  submissionId: '',
  caseId: '',
  statuses: ['RUNNING'],
  submissionDateRange: null,
  includeAll: false
}

const DEFAULT_ROWS = 25

const EVIDENCE_META: Record<Evidence, { label: string; className: string; hint: string }> = {
  LIKELY_COMPLETE: {
    label: 'Likely complete',
    className: 'text-green-600 dark:text-green-400',
    hint: 'LIVE log gone; run log and ATP report written after this task was submitted.'
  },
  ENDED_NO_ATP: {
    label: 'Ended – no new ATP',
    className: 'text-amber-600 dark:text-amber-400',
    hint: 'Job ended (LIVE log gone, run log uploaded) but no ATP report from this run — probably failed. Review the log.'
  },
  STILL_RUNNING: {
    label: 'Still running',
    className: 'text-blue-600 dark:text-blue-400',
    hint: 'LIVE log still exists — the job is still running (or stuck on the compute side).'
  },
  NO_EVIDENCE: {
    label: 'No evidence',
    className: 'text-gray-500 dark:text-neutral-20',
    hint: 'No run log from this submission yet.'
  }
}

const formatEpoch = (value?: number | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

const rowKey = (row: StalledRow) =>
  `${row.submissionId}|${row.workflowRunGroupSysId}|${row.workflowSystemRunId}|${row.taskId}`

const nextStepLabel = (row: StalledRow) =>
  row.nextSteps?.length
    ? row.nextSteps.map(s => s.taskName || s.taskSystemRunId).join(', ')
    : row.nextStepReason || '—'

const getDateRangeBounds = (range: (Date | null)[] | null) => {
  const from = range?.[0] ? new Date(range[0]) : null
  const to = range?.[1] ? new Date(range[1]) : null
  if (from) from.setHours(0, 0, 0, 0)
  if (to) to.setHours(23, 59, 59, 999)
  return { from: from?.getTime(), to: to?.getTime() }
}

const StalledStarccmTasks: FC = () => {
  const [{ data, loading }, fetchStalled] = useGetStalledStarccmTasks()
  const [{ loading: completing }, forceComplete] = useForceCompleteStarccmTasks()

  const [rows, setRows] = useState<StalledRow[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [counts, setCounts] = useState<Partial<Record<Evidence, number>>>({})
  const [selected, setSelected] = useState<StalledRow[]>([])
  const [showConfirm, setShowConfirm] = useState(false)
  const [first, setFirst] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS)
  const [sortField, setSortField] = useState('runningSince')
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(1)

  const { control, handleSubmit, reset, getValues } = useForm<FilterForm>({
    defaultValues: DEFAULT_FILTER_VALUES
  })

  const runSearch = useCallback(
    (paging: { first: number; rows: number; sortField: string; sortOrder: 1 | -1 | 0 }) => {
      const values = getValues()
      const range = getDateRangeBounds(values.submissionDateRange)
      fetchStalled({
        submissionId: values.submissionId || undefined,
        caseId: values.caseId || undefined,
        status: values.statuses?.length ? values.statuses.join(',') : undefined,
        submissionFrom: range.from,
        submissionTo: range.to,
        includeAll: values.includeAll || undefined,
        skip: paging.first,
        limit: paging.rows,
        sortField: paging.sortField,
        sortOrder: paging.sortOrder === -1 ? 'desc' : 'asc'
      }).catch(() => toast.error('Failed to scan StarCCM cases.'))
    },
    [fetchStalled, getValues]
  )

  useEffect(() => {
    const payload = data as
      | { data?: StalledRow[]; totalCount?: number; counts?: Partial<Record<Evidence, number>> }
      | undefined
    if (payload?.data) {
      setRows(payload.data)
      setTotalRecords(payload.totalCount ?? payload.data.length)
      setCounts(payload.counts ?? {})
    }
  }, [data])

  const onSearch = () => {
    setFirst(0)
    setSelected([])
    runSearch({ first: 0, rows: pageSize, sortField, sortOrder })
  }

  const onClear = () => {
    reset(DEFAULT_FILTER_VALUES)
    setFirst(0)
    setSelected([])
    setRows([])
    setTotalRecords(0)
    setCounts({})
  }

  const onPage = (event: DataTablePageEvent) => {
    setFirst(event.first)
    setPageSize(event.rows)
    runSearch({ first: event.first, rows: event.rows, sortField, sortOrder })
  }

  const onSort = (event: DataTableSortEvent) => {
    const nextField = event.sortField || 'runningSince'
    const nextOrder = (event.sortOrder ?? 1) as 1 | -1 | 0
    setSortField(nextField)
    setSortOrder(nextOrder)
    runSearch({ first, rows: pageSize, sortField: nextField, sortOrder: nextOrder })
  }

  const handleForceComplete = async () => {
    try {
      const response = await forceComplete(
        selected.map(row => ({
          submissionId: row.submissionId,
          workflowRunGroupSysId: row.workflowRunGroupSysId,
          workflowSystemRunId: row.workflowSystemRunId,
          taskId: row.taskId
        }))
      )
      const result = response?.data as ForceCompleteResponse | undefined
      const succeeded = result?.succeeded ?? 0
      const failed = result?.failed ?? 0
      const pending = (result?.results ?? []).filter(r => r.nextStepPending)
      const failures = (result?.results ?? []).filter(r => !r.success)

      if (failed === 0) {
        toast.success(`Completed ${succeeded} case${succeeded === 1 ? '' : 's'}.`)
      } else {
        console.error('Force-complete failures:', failures)
        const reasons = Array.from(new Set(failures.map(r => r.message)))
          .slice(0, 2)
          .join('; ')
        toast.error(`Completed ${succeeded}, ${failed} failed${reasons ? `: ${reasons}` : '.'}`, {
          duration: 8000
        })
      }
      if (pending.length > 0) {
        console.warn('Force-complete next step not started yet:', pending)
        toast(
          `Next step hasn't started yet for ${pending.map(r => r.caseId).join(', ')}. ` +
            'Re-check these cases in a few minutes.',
          { icon: '⚠️', duration: 12000 }
        )
      }

      setShowConfirm(false)
      setSelected([])
      runSearch({ first, rows: pageSize, sortField, sortOrder })
    } catch (error) {
      console.error(error)
      toast.error('Failed to complete selected cases.')
    }
  }

  const evidenceBody = (row: StalledRow) => {
    const meta = EVIDENCE_META[row.evidence]
    return (
      <span
        className={`${meta.className} font-medium`}
        title={row.evidenceError ? `${meta.hint} (${row.evidenceError})` : meta.hint}
      >
        {meta.label}
      </span>
    )
  }

  const caseBody = (row: StalledRow) => {
    const value = row.caseName || row.caseId || '—'
    return (
      <div
        className="max-w-40 overflow-x-auto whitespace-nowrap"
        title={`${value} (${row.caseId})`}
      >
        {value}
      </div>
    )
  }

  const latestLogBody = (row: StalledRow) =>
    row.latestLogName ? (
      <span title={`Uploaded ${formatEpoch(row.latestLogModified)}`}>{row.latestLogName}</span>
    ) : (
      '—'
    )

  const atpBody = (row: StalledRow) =>
    row.atpReportName ? (
      <span
        className={row.atpFresh ? '' : 'text-gray-400 dark:text-neutral-30'}
        title={row.atpFresh ? 'Written by this run' : 'Older than this task submission'}
      >
        {formatEpoch(row.atpModified)}
      </span>
    ) : (
      '—'
    )

  const submissionBody = (row: StalledRow) =>
    row.submissionName ? (
      <Link
        to={`/submissions/${encodeURIComponent(row.submissionName)}${
          row.caseId ? `?case=${encodeURIComponent(row.caseId)}` : ''
        }`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gm-blue dark:text-light-blue hover:underline"
        title="Open run in a new tab"
      >
        {row.submissionName}
      </Link>
    ) : (
      row.submissionId
    )

  const selectedCount = selected.length
  const continues = selected.some(row => (row.nextSteps?.length ?? 0) > 0)
  const actionLabel = continues ? 'Mark Complete & Continue' : 'Mark Complete'
  const selectedResetCount = selected.filter(row =>
    RESET_STATUSES.includes(row.status ?? '')
  ).length
  const selectedCancelledCount = selected.filter(row => row.status === 'CANCELLED').length

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-neutral-20">
        StarCCM solves that haven&apos;t completed in the platform (Running by default; pick other
        statuses below). A case is only selectable when its LIVE log is gone and both the run log
        and ATP report were written after the task was submitted.
      </p>

      <form
        onSubmit={handleSubmit(onSearch)}
        className="dark:bg-neutral-80 bg-gray-50 p-4 rounded-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <div className="flex flex-col">
          <label className="text-sm mb-1">Submission ID</label>
          <Controller
            name="submissionId"
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder="Submission ID or name" className="w-full" />
            )}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm mb-1">Case ID</label>
          <Controller
            name="caseId"
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder="Case ID or name" className="w-full" />
            )}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm mb-1">Task Status</label>
          <Controller
            name="statuses"
            control={control}
            render={({ field }) => (
              <MultiSelect
                value={field.value}
                onChange={e => field.onChange(e.value)}
                options={STATUS_OPTIONS}
                placeholder="Running"
                className="w-full"
                pt={MultiSelectCheckboxStyle}
              />
            )}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm mb-1">Submitted</label>
          <Controller
            name="submissionDateRange"
            control={control}
            render={({ field }) => (
              <Calendar
                value={field.value as Date[] | null}
                onChange={e => field.onChange(e.value)}
                selectionMode="range"
                readOnlyInput
                showButtonBar
                placeholder="Start - End"
                className="w-full"
              />
            )}
          />
        </div>
        <div className="flex items-center gap-2">
          <Controller
            name="includeAll"
            control={control}
            render={({ field }) => (
              <Checkbox
                inputId="includeAllStalled"
                checked={field.value}
                onChange={e => field.onChange(e.checked ?? false)}
              />
            )}
          />
          <label htmlFor="includeAllStalled" className="text-sm cursor-pointer">
            Also show still-running / no-evidence cases
          </label>
        </div>
        <div className="flex items-end gap-2 lg:col-span-2">
          <Button type="submit" label="Scan" icon="pi pi-search" loading={loading} />
          <Button
            type="button"
            label="Clear"
            severity="secondary"
            outlined
            className="!border-gray-500 dark:!border-neutral-20"
            pt={{ label: { className: '!text-white' } }}
            onClick={onClear}
          />
        </div>
      </form>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500 dark:text-neutral-20">
          {selectedCount > 0
            ? `${selectedCount} selected`
            : `${counts.LIKELY_COMPLETE ?? 0} likely complete · ${
                counts.ENDED_NO_ATP ?? 0
              } ended without new ATP · ${counts.STILL_RUNNING ?? 0} still running · ${
                counts.NO_EVIDENCE ?? 0
              } no evidence`}
        </span>
        <Button
          label={actionLabel}
          icon="pi pi-check-circle"
          disabled={selectedCount === 0 || completing}
          onClick={() => setShowConfirm(true)}
        />
      </div>

      <DataTable
        value={rows.map(row => ({ ...row, _rowKey: rowKey(row) }))}
        dataKey="_rowKey"
        loading={loading}
        selectionMode="multiple"
        selection={selected}
        onSelectionChange={e => setSelected(e.value as StalledRow[])}
        isDataSelectable={e => (e.data as StalledRow)?.evidence === 'LIKELY_COMPLETE'}
        paginator
        lazy
        first={first}
        rows={pageSize}
        totalRecords={totalRecords}
        rowsPerPageOptions={[25, 50, 100]}
        onPage={onPage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder}
        emptyMessage="No stalled StarCCM cases found. Adjust filters and scan."
        pt={{ paginator: TablePaginatorStyle } as any}
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        <Column field="evidence" header="Evidence" body={evidenceBody} sortable />
        <Column field="submissionName" header="Submission" body={submissionBody} sortable />
        <Column
          field="caseName"
          header="Case"
          body={caseBody}
          sortable
          style={{ width: '10rem', maxWidth: '10rem' }}
        />
        <Column
          field="status"
          header="Status"
          body={(row: StalledRow) => (
            <span
              className={
                RESET_STATUSES.includes(row.status ?? '')
                  ? 'text-red-600 dark:text-red-400'
                  : row.status === 'CANCELLED'
                    ? 'text-gray-500 dark:text-neutral-20'
                    : ''
              }
            >
              {statusLabel(row.status)}
            </span>
          )}
          sortable
        />
        <Column
          field="runningSince"
          header="Submitted"
          body={(row: StalledRow) => formatEpoch(row.runningSince)}
          sortable
        />
        <Column
          header="LIVE Log"
          body={(row: StalledRow) => (row.liveLogName ? 'Present' : 'Absent')}
        />
        <Column field="latestLogModified" header="Run Log" body={latestLogBody} sortable />
        <Column field="atpModified" header="ATP Report" body={atpBody} sortable />
        <Column header="Next Step" body={nextStepLabel} />
        <Column field="lastSubmittedBy" header="Submitted By" />
      </DataTable>

      <Dialog
        header="Confirm Mark Complete"
        visible={showConfirm}
        onHide={() => setShowConfirm(false)}
        style={{ width: '48rem' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              pt={{ label: { className: '!text-white' } }}
              onClick={() => setShowConfirm(false)}
            />
            <Button
              label={actionLabel}
              icon="pi pi-check-circle"
              loading={completing}
              onClick={handleForceComplete}
            />
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p>
            The StarCCM solve task for <strong>{selectedCount}</strong> case
            {selectedCount === 1 ? '' : 's'} will be sent the completion message the platform
            missed, so it marks the task <strong>Completed</strong>
            {continues
              ? ' and starts the next step itself.'
              : '. These cases have no remaining workflows, so nothing else is started.'}{' '}
            Evidence is re-checked on the server first.
          </p>
          <div className="border rounded-md border-gray-200 dark:border-neutral-70 max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100 dark:bg-neutral-90">
                <tr className="text-left">
                  <th className="px-2 py-1 font-medium">Case</th>
                  <th className="px-2 py-1 font-medium">Status</th>
                  <th className="px-2 py-1 font-medium">Run Log</th>
                  <th className="px-2 py-1 font-medium">ATP Report</th>
                  <th className="px-2 py-1 font-medium">Next Step</th>
                </tr>
              </thead>
              <tbody>
                {selected.map(row => (
                  <tr key={rowKey(row)} className="border-t border-gray-100 dark:border-neutral-80">
                    <td className="px-2 py-1 break-words">{row.caseName || row.caseId}</td>
                    <td className="px-2 py-1 break-words">{statusLabel(row.status)}</td>
                    <td className="px-2 py-1 break-words">{row.latestLogName || '—'}</td>
                    <td className="px-2 py-1 break-words">{formatEpoch(row.atpModified)}</td>
                    <td className="px-2 py-1 break-words">{nextStepLabel(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selectedResetCount > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {selectedResetCount} task{selectedResetCount === 1 ? ' is' : 's are'} in Error / Warn
              on Error. They are set back to Running first so the platform accepts the completion,
              and restored if it doesn&apos;t.
            </p>
          )}
          {selectedCancelledCount > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {selectedCancelledCount} task{selectedCancelledCount === 1 ? ' was' : 's were'}{' '}
              cancelled. Completing {selectedCancelledCount === 1 ? 'it' : 'them'} overrides the
              cancellation and starts the next step.
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-neutral-20">
            This waits up to 30 seconds for the platform to confirm each case.
          </p>
        </div>
      </Dialog>
    </div>
  )
}

export default StalledStarccmTasks
